import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();

    window.addEventListener('popstate', this.#handlePopState, {
      signal: this.#historyAbortController.signal,
    });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();

    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty(
        '--dialog-drawer-closing-animation',
        'none'
      );

      await this.closeDialog();

      this.refs.dialog.style.removeProperty(
        '--dialog-drawer-closing-animation'
      );
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (
      this.refs.liveRegion
    );

    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined)
      return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked
     * to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent =
        document.querySelector('shopify-payment-terms')?.shadowRoot;

      const cta =
        installmentsContent?.querySelector('#shopify-installments-cta');

      cta?.addEventListener('click', this.closeDialog, {
        once: true,
      });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);

    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}

/**
 * TERMS & CONDITIONS CHECKBOX LOGIC
 * Uses event delegation so it survives Shopify AJAX cart refreshes
 */

document.addEventListener('change', (event) => {
  const checkbox = event.target;

  if (!(checkbox instanceof HTMLInputElement)) return;
  if (checkbox.id !== 'CartTermsCheckbox') return;

  const checkoutButton = document.getElementById('checkout');
  const error = document.querySelector('.cart-terms__error');

  if (!checkoutButton) return;

  checkoutButton.disabled = !checkbox.checked;

  if (checkbox.checked && error) {
    error.hidden = true;
  }
});

document.addEventListener('click', (event) => {
  const checkoutButton = event.target.closest('#checkout');

  if (!checkoutButton) return;

  const checkbox = document.getElementById('CartTermsCheckbox');
  const error = document.querySelector('.cart-terms__error');

  if (!checkbox?.checked) {
    event.preventDefault();

    if (error) {
      error.hidden = false;
    }
  }
});

/**
 * Ensure checkout starts disabled until checkbox is checked
 * Re-runs after Shopify AJAX updates
 */

function initializeTermsCheckbox() {
  const checkbox = document.getElementById('CartTermsCheckbox');
  const checkoutButton = document.getElementById('checkout');

  if (!checkbox || !checkoutButton) return;

  checkoutButton.disabled = !checkbox.checked;
}

document.addEventListener('DOMContentLoaded', initializeTermsCheckbox);

/**
 * Shopify cart drawers/pages often dispatch section reloads.
 * This catches dynamic re-renders.
 */
document.addEventListener('shopify:section:load', initializeTermsCheckbox);
document.addEventListener('cart:refresh', initializeTermsCheckbox);
document.addEventListener('cart:updated', initializeTermsCheckbox);